import React from 'react';
import clsx from 'clsx';
import {useAbnormalBiomarkers} from '../hooks/useAbnormalBiomarkers';

/**
 * "N Biomarkers Need Attention" — right-sidebar card from
 * `mld-dash-26-v11.psd`.
 *
 * This content used to live inside the BioMatch kit card (as a markers
 * bubble, a count legend and a white chip panel). v11 splits it into a
 * standalone card so the BioMatch card can be purely about the kit being
 * sold, while this one is purely about the patient's own results.
 *
 * Lists EVERY biomarker whose latest valid result is out of range or
 * borderline — independent of BioMatch matching, so the card appears
 * whenever the patient's results warrant it, kit or no kit. Severity
 * comes from the shared classifier SSOT via `useAbnormalBiomarkers`, so
 * chip colors always agree with the chart zones and Lab Trends.
 *
 * At the source artboard's 1920px width the card is 502 x 227px.
 */

/** Soft status-tinted pill with a solid status border, per the PSD
 *  (pink #ffecf2 / #f15e87, cream #faf2e7 / #ffbe7a). Expressed against
 *  the status tokens so tenant themes and the chart zones stay in sync. */
const CHIP_STYLES = {
    high: 'border-mld-status-high bg-mld-status-high/12',
    borderline: 'border-mld-status-borderline bg-mld-status-borderline/20',
} as const;

interface Props {
    className?: string;
}

const BiomarkersNeedAttentionCard: React.FC<Props> = ({className}) => {
    const {isLoading, abnormal, counts} = useAbnormalBiomarkers();

    // Nothing to flag → no card, which avoids a card that reads
    // "0 Biomarkers Need Attention". Deliberately no isError gate — the
    // hook reads the tests cache, and cached data keeps rendering through
    // a failed background refetch (same resilience rule PR #162 applied
    // to hasBioMatchCardContent, so the sidebar cards can't disagree).
    if (isLoading || abnormal.length === 0) return null;

    const total = counts.outOfRange + counts.borderline;

    // Inline card rather than <Card> — the design's 21px radius isn't one of
    // that component's presets. Background stays on the themable token so
    // tenant white-labeling still applies.
    return (
        <section
            className={clsx(
                'rounded-[20px] border border-mld-panel-border bg-mld-menu-background tracking-[-0.02em]',
                className
            )}>
            <div className="px-[34px] pb-[25px] pt-[26px]">
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
                    <div className="flex items-center gap-5">
                        <span className="text-[57px] font-medium leading-none text-mld-text-primary">{total}</span>
                        <p className="text-[21px] font-normal leading-[28px] text-mld-text-primary">
                            Biomarkers
                            <br />
                            Need Attention
                        </p>
                    </div>

                    <ul className="mt-1.5 shrink-0 space-y-2.5 text-[16.7px] leading-none text-mld-text-primary">
                        <li className="flex items-center gap-2">
                            <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-full bg-mld-status-high" />
                            {counts.outOfRange} Out of Range
                        </li>
                        <li className="flex items-center gap-2">
                            <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-full bg-mld-status-borderline" />
                            {counts.borderline} Borderline
                        </li>
                    </ul>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                    {abnormal.map((marker) => (
                        <span
                            key={marker.key}
                            className={clsx(
                                'inline-flex h-[49px] items-center rounded-xl border px-[18px] text-[16.7px] font-medium text-mld-text-primary',
                                marker.tier === 'critical' ? CHIP_STYLES.high : CHIP_STYLES.borderline
                            )}>
                            {marker.name}
                        </span>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default BiomarkersNeedAttentionCard;
